/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable unicorn/no-array-for-each */
import { getDocs } from "firebase/firestore";
import { useCallback, useState } from "react";
import { getEmpresaById, getEmpresasByCategoria } from "@/modules/empresas/firestore";
import { getRegiaoByEmpresaId } from "@/modules/regiao/firestore";
import useUserStore from "@/modules/users/store";
import { CEM } from "@/utils/constants";
import { getMediaGruposCollectionReference } from "../firestore";

type GrupoNota = {
  segment: string;
  value: number | undefined;
  autoAvaliacao: number | undefined;
};

/**
 * @param empresaId - id da empresa
 * @param questionarioId - id do questionário
 * @returns - resultados normalizados
 */
const getAllNotas = async (empresaId: TEmpresa["id"], questionarioId: TQuestionario["id"]) => {
  const query = getMediaGruposCollectionReference(empresaId, questionarioId);
  const resultFound = await getDocs(query);
  return resultFound.docs.map((document_) => document_.data());
};

const mapNotas = (notas: TMediaGrupo[], grupos: Map<string, TGruposDiagnostico>) => {
  const notasMap = new Map<string, TMediaGrupo[]>();
  notas.forEach((nota) => {
    const notas = notasMap.get(nota.grupoId) ?? [];
    notas.push(nota);
    notasMap.set(nota.grupoId, notas);
  });

  return [...grupos.values()].map((grupo) => {
    const notasOfGrupo = notasMap.get(grupo.id) ?? [];
    const notasSum = notasOfGrupo.reduce((a, nota) => a + nota.pesoSum / nota.respostasCount, 0);
    const nota = Math.max(0, ((notasSum / notasOfGrupo.length) * CEM) / (grupo?.pesoNota || 1));

    const arrayNotasAvaliador = notasOfGrupo.filter((nota) => nota.mediaAvaliador !== undefined);
    let notaAvaliador: number;
    const notasAvaliador = arrayNotasAvaliador.reduce((a, nota) => a + nota.mediaAvaliador, 0);
    if (arrayNotasAvaliador.length === 0) {
      notaAvaliador = 0;
    } else {
      const media = notasAvaliador / arrayNotasAvaliador.length;
      notaAvaliador = Math.max(0, media);
    }
    return { segment: grupo.nome, value: notaAvaliador, autoAvaliacao: nota };
  });
};

const mapNotasPrimeiroLugar = (allNotas: TMediaGrupo[], grupos: Map<string, TGruposDiagnostico>) => {
  const targetEmpresaId = "8g9R3CSMqUhVv8iJhoyh";

  // Filtra apenas as notas da empresa específica
  const notasEmpresaTarget = allNotas.filter((nota) => nota.empresaId === targetEmpresaId);
  return [...grupos.values()].map((grupo) => {
    // Busca as notas do grupo específico para a empresa
    const notasDoGrupo = notasEmpresaTarget.filter((nota) => nota.grupoId === grupo.id);

    // Busca a primeira nota que possui mediaAvaliador
    const notaComMediaAvaliador = notasDoGrupo.find((nota) => nota.mediaAvaliador !== undefined);

    // Retorna a mediaAvaliador ou 0 se não encontrar
    const valor = notaComMediaAvaliador?.mediaAvaliador || 0;

    return { segment: grupo.nome, value: valor, autoAvaliacao: undefined };
  });
};

/**
 * Hook para buscar os resultados de uma empresa
 * @param empresaId - id da empresa
 * @returns Retorna um objeto com a função `load` para buscar os resultados e a variável `loading` para indicar se está carregando
 */
function useGetResultEmpresa(empresaId: string = "") {
  const [loading, setLoading] = useState(false);

  const [fases, setFases] = useState<string[]>([]);

  const [resultEmpresa, setResultEmpresa] = useState<GrupoNota[]>([]);
  const [resultGeral, setResultGeral] = useState<GrupoNota[]>([]);
  const [resultRegiao, setResultRegiao] = useState<GrupoNota[]>([]);
  const [resultCategoria, setResultCategoria] = useState<GrupoNota[]>([]);
  const [resultPrimeiroLugar, setResultPrimeiroLugar] = useState<GrupoNota[]>([]);

  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const empresaProjeto = empresaId || activeEmpresaId;

  /* eslint-disable complexity */
  const load = useCallback(
    async (
      questionarioId: TQuestionario["id"],
      projetoId: TProjeto["id"],
      empresaId: TEmpresa["id"],
      grupos: TGruposDiagnostico[]
    ) => {
      if (!empresaProjeto || !questionarioId || !projetoId || !empresaId || grupos.length === 0) {
        return;
      }

      const gruposMap = new Map<string, TGruposDiagnostico>();
      for (const grupo of grupos) {
        gruposMap.set(grupo.id, grupo);
      }

      setLoading(true);
      try {
        const allNotasFound = await getAllNotas(empresaProjeto ?? "", questionarioId);
        setFases([...new Set(allNotasFound.map((item) => item.fase ?? ""))]);
        const calcResultGeral = mapNotas(allNotasFound, gruposMap);

        const notasEmpresa = allNotasFound.filter((nota) => nota.empresaId === empresaId);
        const calcResultEmpresa = mapNotas(notasEmpresa, gruposMap);

        const regiao = await getRegiaoByEmpresaId(empresaProjeto, projetoId, empresaId);
        let calcResultRegiao: GrupoNota[] = [];
        if (regiao) {
          const notasRegiao = allNotasFound.filter((nota) => regiao.empresas.includes(nota.empresaId));
          calcResultRegiao = mapNotas(notasRegiao, gruposMap);
        }

        const empresa = await getEmpresaById(empresaId);
        const empresaData = empresa.data();
        let calcResultEmpresasOfCategoria: GrupoNota[] = [];
        if (empresaData && empresaData.categorias) {
          const empresasOfCategoria = await getDocs(getEmpresasByCategoria(empresaData.categorias));
          const empresasIds = new Set(empresasOfCategoria.docs.map((document_) => document_.id));
          const notasEmpresasOfCategoria = allNotasFound.filter((nota) => empresasIds.has(nota.empresaId));
          calcResultEmpresasOfCategoria = mapNotas(notasEmpresasOfCategoria, gruposMap);
        }

        // Calcula as notas do primeiro lugar
        const calcResultPrimeiroLugar = mapNotasPrimeiroLugar(allNotasFound, gruposMap);

        setResultGeral((calcResultGeral as GrupoNota[]) || []);
        setResultEmpresa((calcResultEmpresa as GrupoNota[]) || []);
        setResultRegiao(calcResultRegiao || []);
        setResultCategoria(calcResultEmpresasOfCategoria || []);
        setResultPrimeiroLugar(calcResultPrimeiroLugar || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [empresaProjeto]
  );

  return {
    load,
    loading,
    resultEmpresa,
    resultGeral,
    resultCategoria,
    resultRegiao,
    resultPrimeiroLugar,
    fases
  };
}

export { useGetResultEmpresa };
